import { useCallback } from 'react';
import type { Moment } from 'moment';
import { calculateTimeWidths, formatEventsForTimeline, parseWebViewMessage } from '@components/wallboard/helpers/itemLineTimeline';
import type { Machine, TimelineEvent } from '@components/wallboard/types';

export interface UseManageItemLineParams {
  machine: Machine;
  index: number;
  config: { width: number };
  timeLine: Record<string, TimelineEvent[]>;
  wallboardSelectedLevel: string;
  wallboardTimelineEndDate: string;
  localTime: (t?: string | Date | number | Moment) => Moment;
  isSameDay: (date: string) => boolean;
  isWeb: boolean;
  triggerShowPopup: (config: any) => void;
  triggerHidePopup: () => void;
}

export interface UseManageItemLineReturn {
  machineId: number;
  shortName: string;
  status: string;
  machineStatus: NonNullable<Machine['machineStatus']>;
  formattedEvents: TimelineEvent[];
  renderProps: { machineIndex: number; machineId: number; isWeb: boolean };
  onMessage: (event: any) => void;
  lineWidth: number;
  hourWidth: number;
  minWidth: number;
  secondWidth: number;
}

const useManageItemLine = ({
  machine,
  index,
  config,
  timeLine,
  wallboardSelectedLevel,
  wallboardTimelineEndDate,
  localTime,
  isSameDay,
  isWeb,
  triggerShowPopup,
  triggerHidePopup,
}: UseManageItemLineParams): UseManageItemLineReturn => {
  const {
    machineStatus: { status },
    machineStatus,
    machineId,
    shortName,
  } = machine;

  const { lineWidth, hourWidth, minWidth, secondWidth } = calculateTimeWidths(config.width);

  const events = timeLine?.[machineId] || [];
  const formattedEvents = formatEventsForTimeline(events, localTime, wallboardSelectedLevel, wallboardTimelineEndDate, isSameDay);

  const renderProps = { machineIndex: index, machineId, isWeb };

  const onMessage = useCallback(
    (event: any) => {
      const data = parseWebViewMessage(event.nativeEvent.data);
      if (!data) return;

      switch (data.type) {
        case 'show': {
          triggerShowPopup({
            show: true,
            item: { ...data.item },
            width: data.width,
            position: data.position,
          });
          break;
        }
        case 'hide': {
          triggerHidePopup();
          break;
        }
      }
    },
    [triggerShowPopup, triggerHidePopup]
  );

  return {
    machineId,
    shortName,
    status,
    machineStatus,
    formattedEvents,
    renderProps,
    onMessage,
    lineWidth,
    hourWidth,
    minWidth,
    secondWidth,
  };
};

export default useManageItemLine;
